import { computed, observable, reaction, runInAction } from "mobx"

import { Model } from "@/lib/Model.ts"

import type { IObservableValue } from "mobx"


export interface ScrollableModel extends Model {
  hasTopShadow: IObservableValue<boolean>
  hasBottomShadow: IObservableValue<boolean>
  scrollbarWidth: IObservableValue<number>

  scrollToTop: (behavior?: ScrollBehavior) => void
  scrollToBottom: (behavior?: ScrollBehavior) => void
  setAutoScroll: (value: boolean) => void

  onContentResize: (entries: ResizeObserverEntry[], force: boolean) => void
  onContainerResize: (entries: ResizeObserverEntry[], force: boolean) => void

  containerElement: IObservableValue<HTMLDivElement | null>
  contentElement: IObservableValue<HTMLDivElement | null>

  containerRefCallback: (element: HTMLDivElement | null) => void
  contentRefCallback: (element: HTMLDivElement | null) => void
}

export class ScrollableModelImpl extends Model implements ScrollableModel {
  readonly containerElement: IObservableValue<HTMLDivElement | null> = observable.box(null)
  readonly contentElement: IObservableValue<HTMLDivElement | null> = observable.box(null)
  readonly scrollbarWidth: IObservableValue<number> = observable.box(0)

  private readonly contentResizeObserver: ResizeObserver
  private readonly containerResizeObserver: ResizeObserver

  private readonly contentMutationObserver: MutationObserver

  private readonly scrollTop = observable.box(0)
  private readonly scrollHeight = observable.box(0)
  private readonly containerHeight = observable.box(0)

  private readonly autoScroll = observable.box(false)
  private readonly isInterruptedByUser = observable.box(false)
  private scrollRequestId: number | null = null

  constructor() {
    super("ScrollableModelImpl")

    this.contentResizeObserver = new ResizeObserver((entries) => {
      this.onContentResize(entries, false)
    })

    this.containerResizeObserver = new ResizeObserver((entries) => {
      this.onContainerResize(entries, false)
    })

    this.contentMutationObserver = new MutationObserver(() => {
      this.onContentResize([], true)
    })

    this.lifetime.add(reaction(
      () => ({ scrollTop: this.scrollTop.get(), isAtBottom: this.isAtBottom.get() }),
      (curr, prev) => {
        if (curr.scrollTop < prev.scrollTop) {
          return runInAction(() => this.isInterruptedByUser.set(true))
        }

        if (curr.isAtBottom) {
          return runInAction(() => this.isInterruptedByUser.set(false))
        }
      }
    ), "ScrollableModelImpl:scrollTop")

    this.lifetime.add(reaction(
      () => [this.scrollHeight.get()],
      () => {
        if (!this.autoScroll.get()) {
          return cancelAnimationFrame(this.scrollRequestId!)
        }

        this.scrollRequestId = requestAnimationFrame(() => {
          this.isInterruptedByUser.get() || this.scrollToBottom("smooth")
        })
      }
    ), "ScrollableModelImpl:scrollHeight")
  }

  readonly hasTopShadow = computed(() => {
    return this.scrollTop.get() > 0
  })

  readonly hasBottomShadow = computed(() => {
    return !this.isAtBottom.get()
  })

  readonly isAtBottom = computed(() => {
    // The "+1" is a small tolerance to account for rounding errors and subpixel differences in scroll calculations
    return this.scrollHeight.get() - this.scrollTop.get() <= this.containerHeight.get() + 1
  })

  readonly scrollToTop = (behavior?: ScrollBehavior) => {
    this.containerElement.get()?.scrollTo({ top: 0, behavior: behavior || "smooth" })
  }

  readonly scrollToBottom = (behavior?: ScrollBehavior) => {
    const containerElement = this.containerElement.get()
    containerElement && containerElement.scrollTo({
      top: containerElement.scrollHeight,
      behavior: behavior || "smooth"
    })
  }

  readonly setAutoScroll = (value: boolean) => {
    runInAction(() => this.autoScroll.set(value))
  }

  readonly onContentResize = (entries: ResizeObserverEntry[], force: boolean) => {
    if (!(entries.length > 0 || force)) {
      return
    }

    const containerElement = this.containerElement.get()
    if (!containerElement) {
      return
    }

    const containerHeight = containerElement.clientHeight || 0
    const scrollHeight = containerElement.scrollHeight || 0

    runInAction(() => {
      this.containerHeight.set(containerHeight)
      this.scrollHeight.set(scrollHeight)
    })
  }

  readonly onContainerResize = (entries: ResizeObserverEntry[], force: boolean) => {
    if (!(entries.length > 0 || force)) {
      return
    }

    const containerElement = this.containerElement.get()
    if (!containerElement) {
      return
    }

    const scrollbarWidth = containerElement.offsetWidth - containerElement.clientWidth

    runInAction(() => {
      this.scrollbarWidth.set(scrollbarWidth)
    })
  }

  private readonly onScroll = () => {
    if (this.containerElement.get()) {
      const scrollTop = this.containerElement.get()?.scrollTop
      runInAction(() => scrollTop !== undefined && this.scrollTop.set(scrollTop))
    }
  }

  readonly containerRefCallback = (element: HTMLDivElement | null) => {
    const containerElement = this.containerElement.get()
    if (containerElement) {
      containerElement.removeEventListener("scroll", this.onScroll)
      this.containerResizeObserver.unobserve(containerElement)
    }

    if (!element) {
      return
    }

    runInAction(() => {
      this.scrollHeight.set(element.scrollHeight)
      this.containerHeight.set(element.clientHeight)
      this.containerElement.set(element)

      const scrollbarWidth = element.offsetWidth - element.clientWidth
      this.scrollbarWidth.set(scrollbarWidth)
    })

    element.addEventListener("scroll", this.onScroll)
    this.containerResizeObserver.observe(element)
  }

  readonly contentRefCallback = (element: HTMLDivElement | null) => {
    const contentElement = this.contentElement.get()
    if (contentElement) {
      this.contentResizeObserver.unobserve(contentElement)
      this.contentMutationObserver.disconnect()
    }

    if (!element) {
      return
    }

    runInAction(() => {
      this.contentElement.set(element)
    })

    this.contentResizeObserver.observe(element)
    this.contentMutationObserver.observe(element, {
      attributes: true,
      childList: true,
      subtree: true
    })
  }

  terminate() {
    this.contentResizeObserver.disconnect()
    this.containerResizeObserver.disconnect()
    this.contentMutationObserver.disconnect()
  }
}
